import {
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconButton,
  IconTrashLine,
  List,
  Modal,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { CyclesService, GoalsService } from "../../services/CyclesService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo";
import { InputDate } from "../InputDate";
import { CycleTypeSelect } from "../Selectors/CycleTypeSelect";
import { CycleSelectLite } from "../Selectors/CyclesSelector";
import { CycleGoalEditModal } from "./CycleGoalEditModal";
import { CycleTypePanel } from "./CycleTypePanel";

export const CycleEditModal = ({
  open,
  cycle,
  cycles,
  setCycles,
  onCloseHandler,
}) => {
  const showNotification = useNotification();
  const [newCycle, setNewCycle] = useState({ ...cycle });
  const [loading, isLoading] = useState(false);
  const [cycleTypePanelOpen, setCycleTypePanelOpen] = useState(false);
  const [cycleGoalPanelOpen, setCycleGoalPanelOpen] = useState(false);
  const [currentCycleType, setCurrentCycleType] = useState(cycle.type);
  const [currentCycleStart, setCurrentCycleStart] = useState(
    cycle.start_date ? cycle.start_date : ""
  );
  const [currentCycleEnd, setCurrentCycleEnd] = useState(
    cycle.end_date ? cycle.end_date : ""
  );
  const [trigger, setTrigger] = useState(true);
  const [updatedGoals, setUpdatedGoals] = useState(false);
  const [editedGoal, setEditedGoal] = useState(null);
  const [cycleGoalToRemove, setCycleGoalToRemove] = useState(null);
  const [openGoalDialog, setOpenGoalDialog] = useState(false);
  const [currentCycleParent, setCurrentCycleParent] = useState(
    cycle.parent_cycle
  );
  const [allCycles, setAllCycles] = useState([]);
  const [cycleLoader, setCycleLoader] = useState(false);

  useEffect(() => {
    if (trigger) {
      let today = new Date();
      if (currentCycleEnd === "") {
        setCurrentCycleEnd(today);
        newCycle.end_date = today;
      }
      if (currentCycleStart === "") {
        setCurrentCycleStart(today);
        newCycle.start_date = today;
      }
      setTrigger(false);
      CyclesService.getCycles().then((cycles) => {
        let cycle_list = [];
        cycles.forEach((cycle) => {
          cycle_list.push({ id: cycle.id, name: cycle.name });
        });
        setAllCycles(cycle_list);
      });
    }
  }, [trigger]);

  const handleClose = () => {
    open = false;
    onCloseHandler();
  };

  const handleSubmit = () => {
    if (
      newCycle.name &&
      newCycle.type &&
      Object.keys(newCycle.type).length !== 0 &&
      newCycle.start_date &&
      newCycle.end_date
    ) {
      isLoading(true);
      const promise = newCycle.id
        ? CyclesService.updateCycle(newCycle)
        : CyclesService.createCycle(newCycle);
      promise
        .then(() => {
          if (newCycle.id) {
            setCycles([
              ...cycles.map((cycle) =>
                cycle.id === newCycle.id ? newCycle : cycle
              ),
            ]);
          } else {
            cycles.unshift(newCycle);
            setCycles([...cycles]);
          }
          isLoading(false);
          open = false;
          onCloseHandler();
          showNotification(t("notifications.cycle.createCycleSuccess"));
        })
        .catch((e) => {
          showNotification(
            `${t("notifications.cycle.createCycleError")} ${e}`,
            ERROR
          );
        });
    } else {
      window.alert("Cycle requires name, type, start date, and end date.");
    }
  };

  const handleCycleTypeButtonClick = () => {
    setCycleTypePanelOpen(true);
  };

  const onCycleTypeSelect = (type) => {
    setCurrentCycleType(type);
    newCycle.type = type;
    setNewCycle({ ...newCycle });
  };

  const onStartDateSelect = (date) => {
    if (date) {
      setCurrentCycleStart(date);
      newCycle.start_date = date;
      setNewCycle({ ...newCycle });
    }
  };

  const onEndDateSelect = (date) => {
    if (date) {
      setCurrentCycleEnd(date);
      newCycle.end_date = date;
      setNewCycle({ ...newCycle });
    }
  };

  function onParentCycleSelect(cycleId) {
    if (cycleId) {
      const option = allCycles.filter((cycle) => cycle.id === cycleId)[0];
      newCycle.parent_cycle = option;
      setNewCycle({ ...newCycle });
      setCurrentCycleParent(newCycle.parent_cycle);
    }
  }

  // const handleEditGoalClick = (goal) => {
  //   if (goal.objectives.length === 0) {
  //     goal.objectives.push({ description: "" });
  //   }
  //   setEditedGoal(goal);
  //   setCycleGoalPanelOpen(true);
  // };

  const addCycleGoal = () => {
    setEditedGoal({ description: "", objectives: [{ description: "" }] });
    setCycleGoalPanelOpen(true);
  };

  const handleEditModalClose = (newCycleGoal) => {
    if (!newCycleGoal || newCycleGoal.description === "") {
      // ignore
    } else if (
      !newCycle.goals ||
      !newCycle.goals.some((element) => element.id === editedGoal.id)
    ) {
      if (!newCycle.goals) {
        newCycle.goals = [];
      }
      newCycle.goals.push(newCycleGoal);
      setNewCycle({ ...newCycle });
      setUpdatedGoals(true);
    } else {
      newCycle.goals.forEach((goal, idx) => {
        if (goal.id === newCycleGoal.id) {
          newCycle.goals[idx] = newCycleGoal;
        }
      });
      setNewCycle({ ...newCycle });
      setUpdatedGoals(true);
    }
    setCycleGoalPanelOpen(false);
    setEditedGoal(null);
  };

  const removeCycleGoal = async () => {
    let objectives_to_remove = cycleGoalToRemove.objectives;
    await GoalsService.deleteGoal(cycleGoalToRemove)
      .then(() => {
        objectives_to_remove.forEach((objective) => {
          GoalsService.deleteGoalObjective(objective);
          setCycleGoalToRemove(null);
          newCycle.goals = newCycle.goals.filter(
            (goal) => goal.id !== cycleGoalToRemove.id
          );
          setNewCycle({ ...newCycle });
          cycle.goals = newCycle.goals;
          setOpenGoalDialog(false);
          setUpdatedGoals(true);
        });
        showNotification(t("notifications.cycle.deleteGoalSuccess"));
      })
      .catch((e) => {
        showNotification(
          `${t("notifications.cycle.deleteGoalError")} ${e}`,
          ERROR
        );
      });
  };

  const getModalHeader = () => (
    <Modal.Header>
      <Grid hAlign="space-between">
        <Grid.Row>
          <Grid.Col>
            <Heading level="h2" as="h1">
              {t("cyclesAndGoals.configure")}
            </Heading>
          </Grid.Col>
          <Grid.Col width="auto">
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={handleClose}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </Modal.Header>
  );

  const getModalBody = () => {
    return (
      <>
        <Grid colSpacing="small" rowSpacing="small" vAlign="middle">
          <Grid.Row key={"edited-cycle-row"}>
            <Grid.Col width={6}>
              <TextInput
                value={newCycle.name ? newCycle.name : ""}
                interaction={"enabled"}
                id={"cycle-type-name"}
                renderLabel={
                  <Text size="small">{t("cyclesAndGoals.name")}</Text>
                }
                onChange={(e) => {
                  newCycle.name = e.currentTarget.value;
                  setNewCycle({ ...newCycle });
                }}
              />
            </Grid.Col>
            <Grid.Col width={6}>
              <CycleSelectLite
                selectHandler={onParentCycleSelect}
                selectedCycle={currentCycleParent}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col width={8}>
              {cycleLoader ? (
                <Skeleton
                  count={1}
                  style={{ height: 40, borderRadius: 5, marginBottom: 10 }}
                />
              ) : (
                <CycleTypeSelect
                  selectHandler={onCycleTypeSelect}
                  selectedCycleType={currentCycleType}
                />
              )}
            </Grid.Col>
            <Grid.Col vAlign="bottom">
              <Button
                id="add-cycle-type-button"
                color="primary"
                onClick={handleCycleTypeButtonClick}
              >
                {t("cyclesAndGoals.cycleType.manageCycleTypes")}
              </Button>
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col>
              <InputDate
                id="date-1-selector"
                label={
                  <Text size="small">{t("cyclesAndGoals.startDate")}</Text>
                }
                selectHandler={onStartDateSelect}
                selectedDate={newCycle.start_date ? newCycle.start_date : ""}
              />
            </Grid.Col>
            <Grid.Col>
              <InputDate
                id="date-2-selector"
                label={<Text size="small">{t("cyclesAndGoals.endDate")}</Text>}
                selectHandler={onEndDateSelect}
                selectedDate={newCycle.end_date ? newCycle.end_date : ""}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
        <div style={{ marginTop: 15 }}>
          <Heading as="h4" level="h4">
            <Text size="small">
              {t("cyclesAndGoals.cycleGoal.goalsAndObjectives")}
            </Text>
          </Heading>

          <div style={{ marginTop: 15 }}>
            <List isUnstyled style={{ Top: 15 }}>
              {newCycle.goals &&
                newCycle.goals.map((goal, key) => {
                  return (
                    <List.Item key={key}>
                      <div
                        style={{
                          backgroundColor: "#F2F8FA",
                          border: "1px solid #333",
                          borderRadius: "10px",
                          padding: "5px 15px",
                          marginBottom: 10,
                        }}
                      >
                        <Flex justifyItems="space-between" alignItems="start">
                          <Flex.Item size="90%">
                            {goal.description}
                            <List>
                              {goal.objectives &&
                                goal.objectives.map((objective, index) => {
                                  return (
                                    <List.Item key={index}>
                                      <div>
                                        <Flex
                                          justifyItems="space-between"
                                          alignItems="center"
                                        >
                                          <Flex.Item shouldShrink shouldGrow>
                                            {objective.description}
                                          </Flex.Item>
                                        </Flex>
                                      </div>
                                    </List.Item>
                                  );
                                })}
                            </List>
                          </Flex.Item>
                          <Flex.Item>
                            {/* <IconButton
                              shape="rectangle"
                              screenReaderLabel={t(
                                "cyclesAndGoals.cycleType.editTagScreenReaderLabel"
                              )}
                              margin="none small none none"
                              onClick={() => handleEditGoalClick(goal)}
                            >
                              <IconEditLine />
                            </IconButton> */}
                            <IconButton
                              shape="rectangle"
                              screenReaderLabel={t(
                                "cyclesAndGoals.cycleType.deleteTagScreenReaderLabel"
                              )}
                              onClick={() => {
                                setCycleGoalToRemove(goal);
                                setOpenGoalDialog(true);
                              }}
                            >
                              <IconTrashLine />
                            </IconButton>
                          </Flex.Item>
                        </Flex>
                      </div>
                    </List.Item>
                  );
                })}
            </List>
            <DialogYesNo
              message={t("cyclesAndGoals.cycleGoal.deleteCycleGoal")}
              openStatus={openGoalDialog}
              onClickNo={() => {
                setCycleGoalToRemove(null);
                setOpenGoalDialog(false);
              }}
              onClickYes={removeCycleGoal}
            />
            <div style={{ float: "right" }}>
              <Button
                id="add-cycle-goal-button"
                color="primary"
                renderIcon={IconAddLine}
                onClick={addCycleGoal}
              >
                {t("cyclesAndGoals.cycleGoal.addCycleGoalButton")}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  };

  const getModalFooter = () => (
    <Modal.Footer>
      {!updatedGoals && (
        <Button margin="0 small 0 0" onClick={handleClose}>
          {t("global.cancel")}
        </Button>
      )}

      <Button
        color="primary"
        type="submit"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
        ) : (
          t("global.submit")
        )}
      </Button>
    </Modal.Footer>
  );

  return (
    <>
      <Modal
        key={"cycle-type-edit-modal"}
        open={open}
        size="large"
        label={t("cyclesAndGoals.cycleType.configure")}
      >
        {getModalHeader()}
        <Modal.Body>{getModalBody()}</Modal.Body>
        {getModalFooter()}
      </Modal>
      <CycleTypePanel
        cycleTypePanelOpen={cycleTypePanelOpen}
        setCycleTypePanelOpen={setCycleTypePanelOpen}
        setTrigger={setTrigger}
        setLoad={setCycleLoader}
      />
      {editedGoal && (
        <CycleGoalEditModal
          cycleGoalPanelOpen={cycleGoalPanelOpen}
          cycleGoal={editedGoal}
          onCloseHandler={handleEditModalClose}
        />
      )}
    </>
  );
};
